import { defineStore } from "pinia";
import accountService from "@/services/account.service";
import { apiConfig } from "@/config/api.config";
import type {
  State,
  City,
  StatesResponse,
  CitiesResponse,
} from "@/types/api.types";

interface LocationState {
  states: State[];
  cities: City[];
  selectedStateId: string;
  selectedCityId: string;
  isLoadingStates: boolean;
  isLoadingCities: boolean;
  statesError: string;
  citiesError: string;
}

export const useLocationStore = defineStore("location", {
  state: (): LocationState => ({
    states: [],
    cities: [],
    selectedStateId: "",
    selectedCityId: "",
    isLoadingStates: false,
    isLoadingCities: false,
    statesError: "",
    citiesError: "",
  }),

  getters: {
    hasStates: (state): boolean => state.states.length > 0,
    hasCities: (state): boolean => state.cities.length > 0,

    selectedState: (state): State | undefined => {
      return state.states.find((s) => s.id === state.selectedStateId);
    },

    selectedCity: (state): City | undefined => {
      return state.cities.find((c) => c.id === state.selectedCityId);
    },

    stateByCode: (state) => {
      return (code: string): State | undefined => {
        return state.states.find((s) => s.code === code);
      };
    },

    cityByName: (state) => {
      return (name: string): City | undefined => {
        return state.cities.find((c) => c.name === name);
      };
    },
  },

  actions: {
    async fetchStates(): Promise<void> {
      if (this.isLoadingStates) return;

      this.isLoadingStates = true;
      this.statesError = "";

      try {
        const response: StatesResponse = await accountService.getStates(
          apiConfig.location.countryId
        );

        if (response?.data && Array.isArray(response.data)) {
          this.states = response.data;
          console.log("States loaded successfully:", this.states.length);
        } else {
          console.warn("Unexpected states response format:", response);
          this.statesError = "Invalid response format";
        }
      } catch (error: any) {
        console.error("Error fetching states:", error);
        this.statesError =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load states. Please try again.";
      } finally {
        this.isLoadingStates = false;
      }
    },

    async fetchCitiesByState(stateId: string): Promise<void> {
      if (this.isLoadingCities) return;

      this.isLoadingCities = true;
      this.citiesError = "";
      this.cities = []; // Clear previous cities

      try {
        const response: CitiesResponse = await accountService.getCitiesByState(
          stateId
        );

        if (response?.data && Array.isArray(response.data)) {
          this.cities = response.data;
          console.log("Cities loaded successfully:", this.cities.length);
        } else {
          console.warn("Unexpected cities response format:", response);
          this.citiesError = "Invalid response format";
        }
      } catch (error: any) {
        console.error("Error fetching cities:", error);
        this.citiesError =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load cities. Please try again.";
      } finally {
        this.isLoadingCities = false;
      }
    },

    setSelectedState(stateId: string): void {
      this.selectedStateId = stateId;
      this.selectedCityId = ""; // Reset city selection
      this.cities = []; // Clear cities

      if (stateId) {
        this.fetchCitiesByState(stateId);
      }
    },

    setSelectedCity(cityId: string): void {
      this.selectedCityId = cityId;
    },

    clearSelection(): void {
      this.selectedStateId = "";
      this.selectedCityId = "";
      this.cities = [];
    },

    reset(): void {
      this.$reset();
    },
  },
});

export default useLocationStore;
